/**
 * Typed env-var access for the Pulse Launcher SPA.
 *
 * Vite injects values via `import.meta.env`. We surface them through this
 * module so consumers get TS types and stable defaults — and so tests can
 * stub by mocking the module.
 */

export interface LauncherEnv {
  /** Self-hosted Supabase origin (production auth). */
  readonly supabaseUrl: string;
  /** Public anon key for the Supabase project. Safe to ship in JS bundle. */
  readonly supabaseAnonKey: string;
  /** Auth portal origin — used as portalUrl for the shared auth-client. */
  readonly authLoginUrl: string;
  /** Agent-hub Worker origin — `GET /v1/me/apps` lives here. */
  readonly agentHubUrl: string;
  readonly e2eSession: boolean;
}

const FALLBACK_AGENT_HUB = "https://agent-hub.pulsebusiness.ai";
const FALLBACK_AUTH_LOGIN = "https://auth.pulsebusiness.ai";
const FALLBACK_SUPABASE = "https://supabase.pulsebusiness.ai";

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function requireEnv(name: string, value: string | undefined): string {
  if (!value || value === "replace-with-anon-key") {
    throw new Error(`pulse-launcher: ${name} is required.`);
  }
  return value;
}

export function readLauncherEnv(): LauncherEnv {
  const env = import.meta.env;
  const e2eSession = env.VITE_LAUNCHER_E2E_SESSION === "true";
  const supabaseAnonKey = readString(env.VITE_SUPABASE_ANON_KEY);

  return {
    supabaseUrl: readString(env.VITE_SUPABASE_URL) ?? FALLBACK_SUPABASE,
    supabaseAnonKey: e2eSession
      ? (supabaseAnonKey ?? "launcher-e2e-anon-key")
      : requireEnv("VITE_SUPABASE_ANON_KEY", supabaseAnonKey),
    authLoginUrl:
      readString(env.VITE_AUTH_LOGIN_URL) ?? FALLBACK_AUTH_LOGIN,
    agentHubUrl:
      readString(env.VITE_AGENT_HUB_URL) ?? FALLBACK_AGENT_HUB,
    e2eSession,
  };
}
